import { useState, useEffect } from 'react';
import './App.css';
import LoginForm from './components/LoginForm';
import JourneyList from './components/trainJourneys';
import VideoPlayer from './components/VideoPlayer';
import TrainDetail from './components/TrainDetail';
import TrainSearch from './components/TrainSearch';
import LiveMap from './components/LiveMap';
import LiveDetect from './components/LiveDetect';
import RecentDetectionsStrip from './components/RecentDetectionsStrip';
import { useLatestDetection, useDetectionIndex } from './components/useLatestDetection';
import { FEATURES } from './config/features';
import mockupICE from './data/mockupdata/traindetail/v1_trains_0d9e5912-4032-410f-bba4-d4a384142803.json';
import mockupS3 from './data/mockupdata/traindetail/v1_trains_ae67be15-1e7d-4e90-a5ba-8f819602f4ac.json';
import mockupLocationsICE from './data/mockupdata/traindetail/v1_journeys_ca26eaf4-7e0f-46b2-896b-107c9cec9241_locations-json.json';

// Mockup mode — set to true to show mockup train detail data on localhost
const MOCKUP_ENABLED = true;

const HLS_URL = window._env_?.REACT_APP_HLS_URL || process.env.REACT_APP_HLS_URL || 'https://live.railview.cadaconcepts.com/hls/stream.m3u8';

const MOCKUP_DATA = {
  ice: {
    train: mockupICE,
    locationsJSON: mockupLocationsICE,
    currentStationUUID: mockupICE.stationRequest?.stationUUID,
  },
  s3: {
    train: mockupS3,
    locationsJSON: null,
    currentStationUUID: mockupS3.stationRequest?.stationUUID,
  },
};

const StationClock = ({ timestamp, size = 72 }) => {
  const d = new Date(timestamp);
  const h = d.getHours() % 12;
  const m = d.getMinutes();
  const s = d.getSeconds();
  const hourDeg = h * 30 + m * 0.5;
  const minDeg = m * 6;
  const secDeg = s * 6;
  return (
    <svg className="station-clock" viewBox="0 0 100 100" width={size} height={size}>
      <circle cx="50" cy="50" r="48" fill="var(--bg-card)" stroke="var(--border-color)" strokeWidth="2" />
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        return <line key={i} x1={50 + 40 * Math.cos(a)} y1={50 + 40 * Math.sin(a)} x2={50 + 45 * Math.cos(a)} y2={50 + 45 * Math.sin(a)} stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" />;
      })}
      {[...Array(60)].map((_, i) => {
        if (i % 5 === 0) return null;
        const a = (i * 6 - 90) * Math.PI / 180;
        return <line key={i} x1={50 + 42 * Math.cos(a)} y1={50 + 42 * Math.sin(a)} x2={50 + 45 * Math.cos(a)} y2={50 + 45 * Math.sin(a)} stroke="var(--text-muted)" strokeWidth="0.8" />;
      })}
      <line x1="50" y1="50" x2="50" y2="22" stroke="var(--text-primary)" strokeWidth="3.5" strokeLinecap="round" transform={`rotate(${hourDeg}, 50, 50)`} />
      <line x1="50" y1="50" x2="50" y2="14" stroke="var(--text-primary)" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${minDeg}, 50, 50)`} />
      <line x1="50" y1="55" x2="50" y2="18" stroke="#e74c3c" strokeWidth="1.2" strokeLinecap="round" transform={`rotate(${secDeg}, 50, 50)`} />
      <circle cx={50 + 32 * Math.sin(secDeg * Math.PI / 180)} cy={50 - 32 * Math.cos(secDeg * Math.PI / 180)} r="3" fill="#e74c3c" />
      <circle cx="50" cy="50" r="3" fill="var(--text-primary)" />
    </svg>
  );
};

function App() {
  const [loggedInUser, setLoggedInUser] = useState(() => localStorage.getItem('railview-user'));
  const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const formatCurrentTime = () => {
    const now = new Date();
    return `${weekdays[now.getDay()]}, ${now.toLocaleString('de-DE')}`;
  };
  const [currentTime, setCurrentTime] = useState(formatCurrentTime);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [selectedVideo, setSelectedVideo] = useState(1);
  const [theme, setTheme] = useState(() => localStorage.getItem('railview-theme') || 'dark');
  const [activeMainTab, setActiveMainTab] = useState('map');
  const [selectedTrainUUID, setSelectedTrainUUID] = useState(null);
  const [selectedJourneyId, setSelectedJourneyId] = useState(null);
  const [selectedStationUUID, setSelectedStationUUID] = useState(null);
  // Handed to the map to focus a train that may not be running right now
  const [focusRequest, setFocusRequest] = useState(null);
  const [mockupTrain, setMockupTrain] = useState('ice');
  const [timetableTab, setTimetableTab] = useState('timetable');
  const [streamLive, setStreamLive] = useState(false);
  const latestDetection = useLatestDetection();
  const detectionIndex = useDetectionIndex();
  const detectLive = latestDetection.isLive;

  const showAllDetections = () => {
    setActiveMainTab('livefeed');
    setSelectedVideo(2);
    setMobileSection('objects');
  };
  const [mobileSection, setMobileSection] = useState('map');

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const showMockup = MOCKUP_ENABLED && isLocalhost && FEATURES.trainDetailSection;

  // Without the timetable column the map is the only full-width section left
  const mapFullWidth = mobileSection === 'map' || !FEATURES.timetableColumn;

  const handleLogin = (username) => {
    localStorage.setItem('railview-user', username);
    setLoggedInUser(username);
  };

  const handleLogout = () => {
    localStorage.removeItem('railview-user');
    setLoggedInUser(null);
  };

  const toggleTheme = () => {
    setTheme(t => t === 'dark' ? 'light' : 'dark');
  };

  // Every route to a train ends on the map: it gets the identity plus, where known, the
  // moment at the station the user came from, and moves the map there if needed.
  const focusTrainOnMap = (request) => {
    setFocusRequest({ ...request, key: Date.now() });
    setActiveMainTab('map');
    setMobileSection('map');
  };

  const handleJourneySelect = (journey) => {
    const sr = journey.stationRequest || {};
    focusTrainOnMap({
      trainUUID: journey.trainUUID || journey.UUID,
      journeyUUID: journey.journey?.journeyUUID,
      operatingDate: journey.operatingDate,
      atISO: sr.arrivalTime_actual || sr.arrivalTime_scheduled
        || sr.departureTime_actual || sr.departureTime_scheduled,
    });
  };

  const handleTrainSearchSelect = (train) => {
    focusTrainOnMap({
      trainUUID: train.UUID || train.trainUUID,
      journeyUUID: train.journey?.journeyUUID,
      operatingDate: train.operatingDate,
    });
  };

  // "Vollansicht öffnen" in the map panel — only reachable while the detail section is on
  const handleMapTrainSelect = (train) => {
    setSelectedTrainUUID(train.globalTrainID);
    setSelectedJourneyId(train.journeyUUID || null);
    setSelectedStationUUID(null);
    setActiveMainTab('detail');
    setMobileSection('detail');
  };

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('railview-theme', theme);
  }, [theme]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatCurrentTime());
      setClockNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loggedInUser) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      {/* Navbar */}
      <nav className="app-navbar">
        <div className="navbar-brand">
          <i className="fa fa-train brand-train-icon"></i>
          <span>RailView</span>
        </div>
        <div className="navbar-right">
          {/* Clock stays — it moved here when the timetable column went away */}
          <span className="navbar-clock">
            <StationClock key={clockNow} timestamp={clockNow} size={34} />
            <span className="navbar-clock-text">{currentTime}</span>
          </span>
          <span className="navbar-user">
            <i className="fa fa-user-circle"></i>
            {loggedInUser}
          </span>
          <button className="btn-theme-toggle" onClick={toggleTheme} title="Toggle theme">
            <i className={`fa ${theme === 'dark' ? 'fa-sun-o' : 'fa-moon-o'}`}></i>
          </button>
          <button className="btn-logout" onClick={handleLogout}>
            <i className="fa fa-sign-out"></i><span className="btn-logout-text"> Logout</span>
          </button>
        </div>
      </nav>

      {/* Side navigation */}
      <nav className="app-sidenav">
        <button
          className={`sidenav-btn ${mobileSection === 'livefeed' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('livefeed'); setSelectedVideo(1); setMobileSection('livefeed'); }}
          title="Livestream"
        >
          <i className="fa fa-video-camera"></i>
          <span>Live</span>
          {streamLive && <span className="sidenav-live-dot"></span>}
        </button>
        <button
          className={`sidenav-btn ${mobileSection === 'objects' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('livefeed'); setSelectedVideo(2); setMobileSection('objects'); }}
          title="Erkannte Objekte"
        >
          <i className="fa fa-eye"></i>
          <span>Objekte</span>
          {detectLive && <span className="sidenav-live-dot"></span>}
        </button>
        {FEATURES.trainDetailSection && (
          <button
            className={`sidenav-btn ${mobileSection === 'detail' ? 'active' : ''}`}
            onClick={() => { setActiveMainTab('detail'); setMobileSection('detail'); }}
            title="Zugdetails"
          >
            <i className="fa fa-train"></i>
            <span>Details</span>
          </button>
        )}
        <button
          className={`sidenav-btn ${mobileSection === 'map' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('map'); setMobileSection('map'); }}
          title="Karte"
        >
          <i className="fa fa-map-o"></i>
          <span>Karte</span>
        </button>
        {FEATURES.timetableColumn && (
          <button
            className={`sidenav-btn sidenav-mobile-only ${mobileSection === 'timetable' ? 'active' : ''}`}
            onClick={() => setMobileSection('timetable')}
            title="Fahrplan"
          >
            <i className="fa fa-clock-o"></i>
            <span>Fahrplan</span>
          </button>
        )}
      </nav>

      {/* Section heading */}
      <div className="section-heading">
        <i className={`fa ${
          mobileSection === 'livefeed' ? 'fa-video-camera' :
          mobileSection === 'objects' ? 'fa-eye' :
          mobileSection === 'detail' ? 'fa-train' :
          mobileSection === 'map' ? 'fa-map-o' :
          'fa-clock-o'
        }`}></i>
        {mobileSection === 'livefeed' ? 'Livestream' :
         mobileSection === 'objects' ? 'Erkannte Objekte' :
         mobileSection === 'detail' ? 'Zugdetails' :
         mobileSection === 'map' ? 'Karte — alle Züge' :
         'Fahrplan'}
        {mobileSection === 'livefeed' && streamLive && <span className="live-badge">Live</span>}
      </div>

      {/* Main content */}
      <div className="app-content">
        <div className="container-fluid">
          <div className="row g-3">

            {/* Left — Video / Detail / Map (map takes the full width) */}
            <div className={`${mapFullWidth ? 'col-12' : 'col-md-8'} mobile-panel ${mobileSection === 'timetable' ? 'mobile-hidden' : ''}`}>
              <div className={`video-container ${activeMainTab === 'map' ? '' : 'video-container-padded'}`}>
                {/* Live Feed — video */}
                {activeMainTab === 'livefeed' && selectedVideo === 1 && (
                  <>
                    <VideoPlayer src={HLS_URL} onStatusChange={setStreamLive} />
                    <RecentDetectionsStrip index={detectionIndex} onShowAll={showAllDetections} />
                  </>
                )}

                {/* Live Feed — erkannte Objekte */}
                {activeMainTab === 'livefeed' && selectedVideo === 2 && (
                  <LiveDetect detection={latestDetection} index={detectionIndex} />
                )}

                {/* Map — only mount when active so Leaflet gets correct container size */}
                {activeMainTab === 'map' && (
                  <LiveMap onTrainSelect={handleMapTrainSelect} focusRequest={focusRequest} />
                )}

                {/* Detail — behind the feature switch, superseded by the map's train panel */}
                {FEATURES.trainDetailSection && activeMainTab === 'detail' && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {showMockup && (
                        <div className="mockup-toggle">
                          <span className="mockup-toggle-label"><i className="fa fa-flask"></i> Mockup</span>
                          <button
                            className={`mockup-toggle-btn ${mockupTrain === 'ice' ? 'active' : ''}`}
                            onClick={() => setMockupTrain('ice')}
                          >
                            ICE 201
                          </button>
                          <button
                            className={`mockup-toggle-btn ${mockupTrain === 's3' ? 'active' : ''}`}
                            onClick={() => setMockupTrain('s3')}
                          >
                            S3
                          </button>
                        </div>
                      )}
                      {showMockup ? (
                        <TrainDetail
                          key={`mockup-${mockupTrain}`}
                          mockupData={MOCKUP_DATA[mockupTrain]}
                          currentStationUUID={MOCKUP_DATA[mockupTrain].currentStationUUID}
                        />
                      ) : (
                        <TrainDetail key={selectedTrainUUID} trainUUID={selectedTrainUUID} currentStationUUID={selectedStationUUID} />
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Right — Timetable, behind the feature switch; it moved into the map panel */}
            {FEATURES.timetableColumn && (
            <div className={`${mapFullWidth ? 'map-hidden-panel' : 'col-md-4'} mobile-panel ${mobileSection !== 'timetable' ? 'mobile-hidden' : ''}`}>
              <div className="timetable">
                <ul className="nav nav-tabs nav-tabs-sub" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${timetableTab === 'timetable' ? 'active' : ''}`}
                      onClick={() => setTimetableTab('timetable')}
                    >
                      <i className="fa fa-clock-o"></i> Fahrplan
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${timetableTab === 'search' ? 'active' : ''}`}
                      onClick={() => setTimetableTab('search')}
                    >
                      <i className="fa fa-search"></i> Zugsuche
                    </button>
                  </li>
                </ul>
                <div className="tab-content mt-3">
                  <div className={`tab-pane fade ${timetableTab === 'timetable' ? 'show active' : ''}`}>
                    <JourneyList
                      onJourneySelect={handleJourneySelect}
                      selectedJourneyId={selectedJourneyId}
                    />
                  </div>
                  <div className={`tab-pane fade ${timetableTab === 'search' ? 'show active' : ''}`}>
                    <TrainSearch
                      onTrainSelect={handleTrainSearchSelect}
                      selectedTrainUUID={selectedTrainUUID}
                    />
                  </div>
                </div>
              </div>
            </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
